import { useEffect, useRef, useState, type FormEvent } from 'react';
import { EMAIL_MAX, NAME_MAX, claimError, cleanEmail, cleanName } from '../../shared/claim';
import type { ClaimRequest } from '../../shared/types';
import { CONSENT_LABEL, DataProtectionNotice } from './DataProtectionNotice';

interface Props {
  extra: string;
  /**
   * Registro a completar con los datos del ganador. null = no hay nada que
   * pedir (Free Fuel a secas, o un premio que no los pide) y entonces esta es
   * la tarjeta de siempre: premio, nota y Done.
   */
  claimId: string | null;
  /** Guarda los datos del ganador. Lo que falle llega como Error con su motivo. */
  onClaim: (claim: ClaimRequest) => Promise<unknown>;
  onClose: () => void;
}

/**
 * Resultado del giro. Siempre se gana gasolina; el extra es la sorpresa.
 *
 * Cuando hay extra, el premio ya esta anotado en el log del servidor y aqui se
 * le anaden el nombre y el correo del ganador, que es a quien hay que entregar
 * el premio. Cerrar sin rellenar no anula el premio: solo deja el registro sin
 * datos de contacto.
 */
export function PrizeModal({ extra, claimId, onClaim, onClose }: Props) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const won = extra.length > 0;
  const asksForContact = claimId !== null;
  /**
   * El titulo es el nombre del premio. Los extras se escriben en /config como
   * anadidos ("+ Service Club® hat"), asi que se le quita el "+" de delante:
   * de titular sobra, pero los "+" de dentro del texto se quedan.
   */
  const title = won ? extra.replace(/^\s*\+\s*/, '') : 'Free Fuel';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [formError, setFormError] = useState('');
  const pendingForm = asksForContact && !saved;

  useEffect(() => {
    // Con formulario, el foco va al primer campo; sin el, al unico boton.
    if (nameRef.current) nameRef.current.focus();
    else closeRef.current?.focus();
  }, []);

  // Al guardar desaparece el formulario: el foco tiene que ir a lo que queda.
  useEffect(() => {
    if (saved) closeRef.current?.focus();
  }, [saved]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (saving || !claimId) return;

    const clean = { name: cleanName(name), email: cleanEmail(email), consent };
    const invalid = claimError(clean);
    if (invalid) return setFormError(invalid);

    setFormError('');
    setSaving(true);
    try {
      await onClaim({ awardId: claimId, ...clean });
      setSaved(true);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Could not save your details. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      // Con el formulario a medias no se cierra por descuido: un roce fuera de
      // la tarjeta borraria lo ya escrito.
      onClick={pendingForm ? undefined : onClose}
    >
      <div
        className={`prize-card${won ? ' is-jackpot' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="prize-title"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="prize-kicker">{won ? 'Jackpot!' : 'You won'}</p>
        <h2 className="prize-title" id="prize-title">
          {title}
        </h2>

        {!asksForContact && (
          <>
            <p className="prize-note">
              {won
                ? 'Show this screen at the stand to claim your prize.'
                : 'Fill up on us — show this screen at the stand.'}
            </p>
            <button className="btn btn-primary" ref={closeRef} onClick={onClose}>
              Done
            </button>
          </>
        )}

        {asksForContact && !saved && (
          <form className="claim-form" onSubmit={submit} noValidate>
            <p className="prize-note">Leave your details and show this screen at the stand.</p>

            <label className="claim-field">
              <span className="claim-label">Name</span>
              <input
                ref={nameRef}
                className="claim-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={NAME_MAX}
                autoComplete="name"
                autoCapitalize="words"
                disabled={saving}
              />
            </label>

            <label className="claim-field">
              <span className="claim-label">Email</span>
              <input
                className="claim-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                maxLength={EMAIL_MAX}
                autoComplete="email"
                autoCapitalize="off"
                spellCheck={false}
                disabled={saving}
              />
            </label>

            <label className="claim-consent">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                disabled={saving}
              />
              <span>{CONSENT_LABEL}</span>
            </label>

            <DataProtectionNotice />

            {formError && (
              <p className="claim-error" role="alert">
                {formError}
              </p>
            )}

            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Claim prize'}
            </button>
            <button className="claim-skip" type="button" onClick={onClose} disabled={saving}>
              Not now
            </button>
          </form>
        )}

        {asksForContact && saved && (
          <>
            <p className="prize-note">
              Saved. Show this screen at the stand to claim your prize.
            </p>
            <button className="btn btn-primary" ref={closeRef} onClick={onClose}>
              Done
            </button>
          </>
        )}
      </div>
    </div>
  );
}
